import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { NetworkStatus } from '@apollo/client';
import { BooleanParam, NumberParam, StringParam, useQueryParam } from 'use-query-params';
import { useQueryParamWithHistory } from '../../../hooks/useQueryParamWithHistory';

import { NexoyaPageInfo, NexoyaPortfolioParentContent } from 'types';
import { usePortfolio } from '../../../context/PortfolioProvider';
import { useTeam } from '../../../context/TeamProvider';
import { useSidePanelState } from '../../../components/SidePanel';
import { TableStyled } from '../styles/OptimizationProposal';
import { TableManager } from '../../../components/Table/TableManager';
import { PaginationControls } from '../../../components/Table/PaginationControls';
import { ACTIONS_HEADER_ID } from '../components/OptimizationProposal/columns';
import { getColumns } from '../components/Content/Table/columns';
import { getData } from '../components/Content/Table/data-table';
import MultipleSwitch from '../../../components/MultipleSwitchFluid';
import NoDataFound from '../NoDataFound';
import SvgKpi from '../../../components/icons/Kpi';
import { ContentFilter } from '../components/ContentFilter';
import { ContentFilterChips } from '../ContentFilterChips';
import { Skeleton } from '../../../components-ui/Skeleton';
import { cn } from '../../../lib/utils';
import { useTableHeight } from '../../../hooks/useTableHeight';

// Queries and hooks
import { useFunnelStepsV2Query } from '../../../graphql/funnelSteps/queryFunnelSteps';
import { useContentRuleQuery } from '../../../graphql/portfolioRules/queryContentRules';
import { useImpactGroupRuleQuery } from '../../../graphql/portfolioRules/queryImpactGroupRules';
import { useAttributionRuleQuery } from '../../../graphql/portfolioRules/queryAttributionRules';
import { usePortfolioParentContentsQuery } from '../../../graphql/content/queryPortfolioContents';
import { useProviderMetricOptionsQuery } from '../../../graphql/content/queryContentMetricOptions';
import usePortfolioFeatureFlag from '../../../components/PortfolioFeatureSwitch/usePortfolioFeatureFlag';
import { useContentLabelCategories } from '../../../hooks/useContentLabelCategories';
import { useContentLabelAssignmentsQuery } from '../../../graphql/contentLabels/queryContentLabelAssignments';
import { useContentLabelAssignments } from '../components/Content/useContentLabelAssignments';
import { toContentLabelRowIndex } from '../components/Content/contentLabelCells';
import { useReconcileContentLabelsFilter } from './useReconcileContentLabelsFilter';
import { useReconcileLegacyLabelsFilter } from './useReconcileLegacyLabelsFilter';
import { useLabelsFlags } from '../../settings/labels/useLabelsFlags';

// State stores
import { useContentFilterStore } from '../../../store/content-filter';
import { useContentRulesStore } from '../../../store/content-rules';
import { useAttributionRulesStore } from '../../../store/attribution-rules';
import { useImpactGroupRulesStore } from '../../../store/impact-group-rules';

// Constants and utils
import { PORTFOLIO_FEATURE_FLAGS } from '../../../constants/featureFlags';
import { buildPortfolioContentFilterVariables } from '../utils/content';

// Components
import { BulkActionsFloatingBar } from './BulkActionsFloatingBar';
import ContentEdit from '../components/Content/ContentEdit';
import { ContentEditWizard } from '../components/Content/Wizard/ContentEditWizard';
import { VirtualizerExtendedTable } from '../../../components/Table/VirtualizerExtendedTable';
import { Can } from '../../../Auth/rbac';

type Props = {
  dateFrom: Date;
  dateTo: Date | string;
  portfolioId: number;
};

const ContentEditWrapperStyled = styled.div<{ useAbsolutePosition?: boolean }>`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
  ${({ useAbsolutePosition }) =>
    useAbsolutePosition &&
    css`
      position: absolute;
      top: 16px;
      right: 0;
    `}
`;

export const LoadingWrapStyled = styled.div`
  padding-top: 20px;
  height: 100vh;

  & > div {
    margin-bottom: 15px;
    background: #f4f6f7;
    height: 40px;

    &:nth-child(1) {
      opacity: 1;
    }
    &:nth-child(2) {
      opacity: 0.75;
    }
    &:nth-child(3) {
      opacity: 0.5;
    }
    &:nth-child(4) {
      opacity: 0.25;
    }
  }
`;

const TABLE_VIEW_OPTIONS = [
  { id: 'simple', text: 'Simple' },
  { id: 'advanced', text: 'Advanced' },
];

function Content({ dateFrom, dateTo, portfolioId }: Props) {
  const [useLegacySearchScreen] = useQueryParam('useLegacySearchScreen', BooleanParam);
  const [queryParamTeamId] = useQueryParam('team_id', NumberParam);
  const [loading, setLoading] = useState<boolean>(true);
  const [isMounted, setIsMounted] = useState(false);
  const [tableViewParam, setTableViewParam] = useQueryParamWithHistory('tableView', StringParam);
  const tableView = (tableViewParam as 'simple' | 'advanced') || 'simple';
  const setTableView = (value: 'simple' | 'advanced') => setTableViewParam(value);

  const [visibleFunnelStepIds, setVisibleFunnelStepIds] = useState<number[]>([]);

  // Refs for measuring fixed heights
  const headerRef = useRef<HTMLDivElement>(null);
  const filtersRef = useRef<HTMLDivElement>(null);
  const paginationRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate table height based on viewport
  const { tableHeight } = useTableHeight({
    containerRef,
    fixedHeightRefs: {
      header: headerRef,
      filters: filtersRef,
      pagination: paginationRef,
    },
    fixedHeights: {
      padding: 32, // Additional padding/margins
    },
    minHeight: 300,
    maxHeight: '90vh',
  });

  // Pagination state
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [pageInfo, setPageInfo] = useState<NexoyaPageInfo>(null);
  const [pageCount, setPageCount] = useState<number>(0);
  const [paginatedContents, setPaginatedContents] = useState<Record<number, NexoyaPortfolioParentContent[]>>({ 0: [] });

  // Selection state
  const [selectedContentIds, setSelectedContentIds] = useState<number[]>([]);
  const [lastSelectedContentId, setLastSelectedContentId] = useState<number | null>(null);

  // Hooks
  const { isOpen } = useSidePanelState();
  const { teamId } = useTeam();
  const filterStore = useContentFilterStore();
  const { pageSize, setPageSize } = useContentFilterStore();
  const { setContentRules } = useContentRulesStore();
  const { setAttributionRules } = useAttributionRulesStore();
  const { setImpactGroupRules } = useImpactGroupRulesStore();

  const {
    portfolioV2Info: {
      meta: { data: portfolioMeta },
    },
  } = usePortfolio();

  // Feature flags
  const { isFeatureEnabled: hasSelfServicePortfolioFeatureEnabled } = usePortfolioFeatureFlag([
    PORTFOLIO_FEATURE_FLAGS.SELF_SERVICE_PORTFOLIO,
  ]);
  const isAttributed = portfolioMeta?.isAttributed;
  const { legacyEnabled: legacyLabelsEnabled } = useLabelsFlags();

  // GraphQL queries
  const { data: providerMetricOptionsData, loading: metricOptionsLoading } = useProviderMetricOptionsQuery({
    portfolioId,
    skip: tableView === 'simple',
  });

  const { data, fetchMore, networkStatus } = usePortfolioParentContentsQuery({
    ...buildPortfolioContentFilterVariables(teamId, portfolioId, filterStore, { legacyLabelsEnabled }),
    onCompleted: () => setLoading(false),
  });

  // A stored legacy label filter or label-name sort the user can no longer see or clear.
  useReconcileLegacyLabelsFilter({ legacyLabelsEnabled });

  const { data: funnelStepsData } = useFunnelStepsV2Query({ portfolioId });
  const funnelSteps = funnelStepsData?.portfolioV2?.funnelSteps || [];

  // Rules queries
  useContentRuleQuery({
    portfolioId,
    onCompleted: (data) => setContentRules(data?.portfolioV2?.contentRules),
    skip: !hasSelfServicePortfolioFeatureEnabled,
  });

  useImpactGroupRuleQuery({
    portfolioId,
    skip: !hasSelfServicePortfolioFeatureEnabled,
    onCompleted: (data) => setImpactGroupRules(data?.portfolioV2?.impactGroupRules),
  });

  useAttributionRuleQuery({
    portfolioId,
    skip: !isAttributed,
    onCompleted: (data) => setAttributionRules(data?.portfolioV2?.attributionRules),
  });

  // Effects
  useEffect(() => {
    if (networkStatus === NetworkStatus.ready && data?.portfolioV2?.portfolioParentContents) {
      const contents = data.portfolioV2.portfolioParentContents.edges?.map((edge) => edge.node) || [];
      setPaginatedContents((prev) => ({ ...prev, [pageIndex]: contents }));
      setPageInfo(data.portfolioV2.portfolioParentContents.pageInfo);
      setPageCount(data.portfolioV2.portfolioParentContents?.totalPages);
      setSelectedContentIds([]);
    }
  }, [data, networkStatus, pageIndex, pageSize]);

  // Reset page index if we're out of bounds after filter change
  useEffect(() => {
    if (pageIndex >= pageCount) {
      setPageIndex(0);
    }
  }, [pageCount, pageIndex]);

  // Component mount and keyboard shortcut
  useEffect(() => {
    setTimeout(() => setIsMounted(true));

    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
        event.preventDefault();
        document.getElementById('content-search')?.focus();
      }
    };

    if (!isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    // Reset visible funnel steps when table view changes or when funnel steps data changes
    if (tableView === 'advanced' && funnelSteps.length > 0) {
      setVisibleFunnelStepIds([]);

      // Progressively add funnel steps with calculated timeout
      funnelSteps.forEach((step, index) => {
        setTimeout(
          () => {
            setVisibleFunnelStepIds((prev) => [...prev, step.funnelStepId]);
          },
          250 * (index + 1),
        );
      });
    }
  }, [tableView, funnelSteps, pageSize, paginatedContents[pageIndex]?.length]);

  // Handler functions
  const handlePageChange = async (page) => {
    setLoading(true);
    setPageIndex(page);

    const response = await fetchMore({
      variables: {
        after: page > pageIndex ? pageInfo?.endCursor : null,
        before: page < pageIndex ? pageInfo?.startCursor : null,
        first: page > pageIndex ? pageSize : null,
        last: page < pageIndex ? pageSize : null,
      },
    });

    if (response.data) {
      const contents = response.data.portfolioV2?.portfolioParentContents?.edges?.map((edge) => edge.node) || [];
      setPaginatedContents((prev) => ({ ...prev, [page]: contents }));
      setPageInfo(response.data.portfolioV2?.portfolioParentContents?.pageInfo);
      setLoading(false);
    }
  };

  const handlePageSizeChange = (newSize: number) => {
    setLoading(true);
    setPageSize(newSize);
    setPageIndex(0);
    setPaginatedContents({ 0: [] });

    fetchMore({
      variables: { first: newSize },
    }).finally(() => setLoading(false));
  };

  const handleCheckboxAction = (contentId: number, shiftKey: boolean) => {
    if (shiftKey && lastSelectedContentId !== null) {
      // Find the indices of the current and last selected content
      const contentList = paginatedContents[pageIndex].map((pc) => pc.content?.contentId);
      const currentIndex = contentList.indexOf(contentId);
      const lastIndex = contentList.indexOf(lastSelectedContentId);

      if (currentIndex !== -1 && lastIndex !== -1) {
        // Determine the range to select
        const start = Math.min(currentIndex, lastIndex);
        const end = Math.max(currentIndex, lastIndex);

        // Get the IDs of content in the range
        const contentIdsToToggle = contentList.slice(start, end + 1).filter((id) => id !== undefined);

        // Add these IDs to the selected list if not already selected
        setSelectedContentIds((prev) => {
          // Get unique IDs that aren't already in the selection
          const newSelectionSet = new Set([...prev, ...contentIdsToToggle]);
          return Array.from(newSelectionSet);
        });
      }
    } else {
      setSelectedContentIds((prev) => {
        if (prev.includes(contentId)) {
          return prev.filter((id) => id !== contentId);
        }
        return [...prev, contentId];
      });
      setLastSelectedContentId(contentId);
    }
  };

  const handleSelectAllContents = () => {
    if (paginatedContents[pageIndex]?.length === 0) return;

    const allContentIds = paginatedContents[pageIndex].map((pc) => pc.content?.contentId).filter(Boolean);
    if (
      allContentIds.length === selectedContentIds.length &&
      allContentIds.every((id) => selectedContentIds.includes(id))
    ) {
      setSelectedContentIds([]);
    } else {
      setSelectedContentIds(allContentIds);
    }
  };

  /**
   * Content Labels v2 — one column per category (fig4), behind the team flag.
   *
   * `useContentLabelCategories` is the shared seam: with the flag off it SKIPS
   * the read entirely and answers `[]`, so everything below collapses to the
   * table this tab has always rendered.
   */
  const {
    categories: contentLabelCategories,
    loading: contentLabelCategoriesLoading,
    error: contentLabelCategoriesError,
  } = useContentLabelCategories(portfolioId);
  const hasContentLabelCategories = contentLabelCategories.length > 0;

  // A selected label id this read no longer has (deleted, or the flag off) must not filter the table.
  useReconcileContentLabelsFilter({
    categories: contentLabelCategories,
    loading: contentLabelCategoriesLoading,
    error: contentLabelCategoriesError,
  });

  /**
   * The ids of the page ON SCREEN, which is what the assignments read is keyed
   * by. Memoised off `paginatedContents[pageIndex]`, a stable state reference,
   * so paging is the only thing that moves it.
   */
  const pageContents = paginatedContents[pageIndex];
  const pageContentIds = useMemo(
    () =>
      (pageContents ?? [])
        .map((pc) => pc?.content?.contentId)
        .filter((contentId): contentId is number => typeof contentId === 'number'),
    [pageContents],
  );

  const { data: contentLabelAssignmentsData } = useContentLabelAssignmentsQuery({
    contentIds: pageContentIds,
    skip: !hasContentLabelCategories,
  });

  const contentLabelRows = useMemo(
    () => toContentLabelRowIndex(contentLabelAssignmentsData?.listContentsByIds),
    [contentLabelAssignmentsData],
  );

  /** ONE writes hook for the whole table — see `useContentLabelAssignments`. */
  const {
    pendingCategoryId: pendingContentLabelCategoryId,
    assign: assignContentLabel,
    remove: removeContentLabel,
  } = useContentLabelAssignments();

  const contentLabels = hasContentLabelCategories
    ? {
        categories: contentLabelCategories,
        rows: contentLabelRows,
        // `!data`, not `loading`: a page the reader has not seen yet has no
        // assignments to draw and gets skeletons, but the post-write REFETCH
        // (same ids, same variables, `data` retained) must not flash the whole
        // column back to skeletons over a menu the reader just used. A FAILED
        // read has no `data` either, and holds too — every cell would otherwise
        // fall back to a live "+ Add label" over a row that may be labelled.
        loading: !contentLabelAssignmentsData,
        pendingCategoryId: pendingContentLabelCategoryId,
        onAssign: assignContentLabel,
        onRemove: removeContentLabel,
      }
    : undefined;

  // Helper functions for table data
  const hasLabels = legacyLabelsEnabled && portfolioMeta?.labels?.length > 0;
  const hasBidStrategy = paginatedContents?.[pageIndex]?.some((c) => c?.content?.biddingStrategy);
  const hasBudgetLimits = paginatedContents?.[pageIndex]?.some((c) => c?.budgetMin || c?.budgetMax);
  const hasTargetLimits = paginatedContents?.[pageIndex]?.some((c) => c?.minTargetRoasPercent);

  const getProcessedColumns = (loading: boolean) => {
    const baseColumns = getColumns({
      funnelSteps: funnelSteps.filter((step) => visibleFunnelStepIds.includes(step.funnelStepId)),
      hasLabels,
      hasBidStrategy,
      hasBudgetLimits,
      hasTargetLimits,
      isAttributed: portfolioMeta?.isAttributed,
      tableView,
      hasSelfServicePortfolioFeatureEnabled,
      handleSelectAllContents,
      allContentsSelected: selectedContentIds.length
        ? paginatedContents[pageIndex]?.every((pc) => selectedContentIds.includes(pc.content?.contentId))
        : false,
      contentLabelCategories,
    });
    if (loading) {
      return baseColumns.map((column) => {
        // @ts-expect-error
        if (column.columns) {
          return {
            ...column,
            // @ts-expect-error
            columns: column.columns.map((subColumn) => ({
              ...subColumn,
              Cell: () => <Skeleton className="h-5 w-full" />,
            })),
          };
        }
        return {
          ...column,
          Cell: () => <Skeleton className="h-5 w-full" />,
        };
      });
    }
    return baseColumns;
  };

  const getSkeletonRow = () => ({
    content: {},
    subRows: [],
  });

  const tableData = loading
    ? Array.from({ length: 20 }, getSkeletonRow)
    : getData({
        funnelSteps,
        portfolioContents: paginatedContents[pageIndex] || [],
        handleCheckboxAction,
        providerMetricOptions: providerMetricOptionsData?.portfolioV2?.providerMetricOptions,
        metricOptionsLoading,
        isContentSelected: (contentId: number) => selectedContentIds.includes(contentId),
        isAdvancedView: tableView === 'advanced',
        teamId: queryParamTeamId,
        portfolioInfo: {
          start: portfolioMeta?.start,
          end: portfolioMeta?.end,
          portfolioId: portfolioMeta?.portfolioId,
        },
        contentLabels,
      });

  return (
    <div ref={containerRef}>
      {/* Header */}
      <div ref={headerRef} className="mb-7 ml-1 flex justify-between">
        <div>
          <div className="text-[20px] font-medium tracking-normal">Content</div>
          <div className="text-md font-normal text-neutral-500">See and manage all contents in your portfolio.</div>
        </div>
        <Can permission="content.edit">
          <ContentEditWrapperStyled useAbsolutePosition={true}>
            {useLegacySearchScreen ? <ContentEdit dateTo={dateTo} dateFrom={dateFrom} /> : <ContentEditWizard />}
          </ContentEditWrapperStyled>
        </Can>
      </div>

      {/* Filter and View Controls */}
      <div ref={filtersRef} className="mb-4 flex w-full justify-between">
        <div className="flex flex-col gap-1">
          <div className="flex gap-2">
            <ContentFilter contentLabelCategories={contentLabelCategories} legacyLabelsEnabled={legacyLabelsEnabled} />
          </div>
          <ContentFilterChips
            contentLabelCategories={contentLabelCategories}
            legacyLabelsEnabled={legacyLabelsEnabled}
          />
        </div>

        <div className="flex flex-col items-center gap-1">
          <span className="text-[9px] uppercase text-neutral-300">Table view</span>
          <MultipleSwitch
            sections={TABLE_VIEW_OPTIONS}
            initial={tableView}
            current={tableView}
            onToggle={setTableView}
          />
        </div>
      </div>

      {/* Table or No Data */}
      {isMounted && (
        <>
          {loading || paginatedContents[pageIndex]?.length ? (
            <TableStyled maxHeight="90vh">
              <VirtualizerExtendedTable
                tableId="content_table"
                disableManager={false}
                disableExpanded={false}
                disablePagination
                data={tableData}
                columns={getProcessedColumns(loading)}
                tableView={tableView}
                tableHeight={tableHeight}
                renderTableManager={({
                  columns,
                  getToggleHideAllColumnsProps,
                  toggleHideAllColumns,
                  setStickyColumns,
                  stickyColumns,
                }) => (
                  <TableManager
                    idsNotAllowedToHide={[ACTIONS_HEADER_ID, 'expander', 'content']}
                    columns={columns}
                    getToggleHideAllColumnsProps={getToggleHideAllColumnsProps}
                    toggleHideAllColumns={toggleHideAllColumns}
                    setStickyColumns={setStickyColumns}
                    stickyColumns={stickyColumns}
                    depth={0}
                  />
                )}
              />
            </TableStyled>
          ) : (
            <NoDataFound
              icon={<SvgKpi />}
              title="There are no contents assigned in this portfolio."
              subtitle="Please select the 'Edit content' button to add contents. Once you do, you will see them here."
            />
          )}

          {/* Pagination */}
          <div ref={paginationRef} className={cn('flex justify-end', selectedContentIds.length ? 'mb-20' : '')}>
            <PaginationControls
              showFirstPageButton={false}
              showLastPageButton={false}
              gotoPage={handlePageChange}
              canPreviousPage={pageIndex > 0}
              canNextPage={pageInfo?.hasNextPage}
              pageCount={pageCount}
              pageIndex={pageIndex}
              pageSize={pageSize}
              pageOptions={Array.from({ length: pageCount }, (_, i) => i)}
              setPageSize={handlePageSizeChange}
              nextPage={() => handlePageChange(pageIndex + 1)}
              previousPage={() => handlePageChange(pageIndex - 1)}
            />
          </div>

          {/* Bulk Actions */}
          {selectedContentIds.length > 0 && (
            <BulkActionsFloatingBar
              resetContentSelection={() => setSelectedContentIds([])}
              legacyLabelsEnabled={legacyLabelsEnabled}
              contents={paginatedContents?.[pageIndex]?.filter((pc) =>
                selectedContentIds.includes(pc.content?.contentId),
              )}
            />
          )}
        </>
      )}
    </div>
  );
}

export default Content;
